import { ContextStore } from 'bernie-core';

import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { URL_PARAM_TO_QUERY_ID_MAPPER } from 'stores/models';

import { MarketingChannels } from 'constants/marketing-channels';
import { MarketingPrices } from 'constants/marketing-prices';

import {
  adaptCountsFromStrings,
  adaptSelectionFromEnum,
  adaptSelectionFromString,
  adaptSelectionsFromEnums,
  adaptSelectionsFromStrings,
} from './adapter-utils';

import {
  AgencyBusinessModel,
  CleaningAndSafetyPractice,
  CommissionTier,
  DealType,
  Listings,
  MealPlanFilterAmenity,
  NumberValueInput,
  PaginationInput,
  PaymentType,
  Points,
  PrivacyTrackingState,
  PropertyFilterAccessibility,
  PropertyFilterAmenity,
  PropertySearchCriteriaInput,
  PropertySort,
  SelectedValueInput,
  StructureType,
  TravelerType,
} from '__generated__/api';
import { getDateRangeInput, getDestinationInput, getRooms, getSearchCriteriaSecondaryBooleans } from './search-input-adapter';

export interface ExtendedCriteria extends SearchCriteriaModel {
  authSuccessMsg?: string;
  // eslint-disable-next-line camelcase
  exp_dp?: string;
  // eslint-disable-next-line camelcase
  exp_tx?: string;
  rfrr?: string;
  // eslint-disable-next-line camelcase
  top_cur?: string;
  // eslint-disable-next-line camelcase
  top_dp?: string;
  // eslint-disable-next-line camelcase
  trv_bp?: string;
  // eslint-disable-next-line camelcase
  trv_curr?: string;
  // eslint-disable-next-line camelcase
  trv_dp?: string;
}

export function adaptSearchCriteria(
  context: ContextStore,
  extendedCriteria: ExtendedCriteria,
  adsOnly: boolean,
  searchPagination?: PaginationInput
): PropertySearchCriteriaInput {
  const criteria = { ...extendedCriteria } as ExtendedCriteria;

  Object.keys(criteria).forEach(key => {
    if (criteria[key] === '' || (Array.isArray(criteria[key]) && criteria[key].length === 0)) {
      delete criteria[key];
    }
  });

  const searchCriteria = {
    primary: {
      dateRange: getDateRangeInput(criteria),
      destination: getDestinationInput(criteria),
      rooms: getRooms(criteria),
    },
    secondary: {
      counts: [
        ...adaptCountsFromStrings('bedroomFilter', 'bedroomFilter', criteria),
        ...adaptPagination(searchPagination),
        ...adaptCountsFromStrings('price', 'price', criteria),
      ].filter(count => count),
      booleans: getSearchCriteriaSecondaryBooleans(criteria, URL_PARAM_TO_QUERY_ID_MAPPER),
      selections: [
        adaptSelectionFromEnum('sort', 'sort', criteria, PropertySort),
        adaptSelectionFromString('chain', 'chain', criteria),
        adaptSelectionFromString('regionId', 'neighborhood', criteria),
        adaptSelectionFromString('poi', 'poi', criteria),
        adaptSelectionFromString('propertyName', 'hotelName', criteria),
        adaptSelectionFromString('hotelName', 'hotelName', criteria),
        adaptSelectionFromString('semdtl', 'semdtl', criteria),
        adaptSelectionFromString('theme', 'theme', criteria),
        adaptSelectionFromString('paandi', 'paandi', criteria),
        adaptSelectionFromString('userIntent', 'userIntent', criteria),
        adaptSelectionFromString('guestRating', 'guestRating', criteria),
        adaptTrackingState(context),
        ...adaptSelectionsFromEnums('accessibility', 'accessibility', criteria, PropertyFilterAccessibility),
        ...adaptSelectionsFromStrings('bedroomFilter', 'bedroomFilter', criteria).filter(selection => selection),
        ...adaptSelectionsFromEnums('amenities', 'amenities', criteria, PropertyFilterAmenity),
        ...adaptSelectionsFromEnums('commissionTiers', 'commissionTier', criteria, CommissionTier),
        ...adaptSelectionsFromEnums('agencyBusinessModels', 'agencyBusinessModel', criteria, AgencyBusinessModel),
        ...adaptSelectionsFromEnums('deals', 'deals', criteria, DealType),
        ...adaptSelectionsFromEnums('mealPlan', 'mealPlan', criteria, MealPlanFilterAmenity),
        ...adaptSelectionsFromEnums('paymentType', 'paymentType', criteria, PaymentType),
        ...adaptSelectionsFromEnums('cleaningAndSafetyPractices', 'cleaningAndSafetyPractices', criteria, CleaningAndSafetyPractice),
        ...adaptSelectionsFromStrings('rewards', 'rewards', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('starRating', 'star', criteria).filter(selection => selection),
        ...adaptSelectionsFromStrings('star', 'star', criteria).filter(selection => selection),
        ...adaptSelectionsFromEnums('structureTypes', 'lodging', criteria, StructureType),
        ...adaptSelectionsFromEnums('lodging', 'lodging', criteria, StructureType),
        ...adaptSelectionsFromEnums('travelerType', 'travelerType', criteria, TravelerType),
        ...adaptMarketingInfo(criteria).filter(selection => selection),
        ...adaptPropertyShopOptions(criteria.useRewards, adsOnly),
      ].filter(selection => selection),
    },
  };

  return searchCriteria;
}

function adaptMarketingInfo(criteria: ExtendedCriteria): SelectedValueInput[] {
  return Object.keys(criteria).map(id => {
    const value = criteria[id];

    if (value && (id.toLowerCase() in MarketingChannels || id.toLowerCase() in MarketingPrices)) {
      return { id, value };
    }

    return undefined;
  });
}

function adaptPropertyShopOptions(useRewards: string, adsOnly: boolean): SelectedValueInput[] {
  const selections: SelectedValueInput[] = [];

  let points;

  if (useRewards === 'true') {
    points = Points.SHOP_WITH_POINTS;
  } else if (useRewards === 'false') {
    points = Points.SHOP_WITHOUT_POINTS;
  }

  if (points) {
    selections.push({ id: 'useRewards', value: points });
  }

  if (adsOnly) {
    selections.push({ id: 'listings', value: Listings.ADS_ONLY });
  }

  return selections;
}

function adaptPagination(searchPagination?: PaginationInput): NumberValueInput[] {
  if (searchPagination) {
    return [
      { id: 'resultsStartingIndex', value: searchPagination.startingIndex },
      { id: 'resultsSize', value: searchPagination.size },
    ];
  }

  return [];
}

function adaptTrackingState(context: ContextStore): SelectedValueInput {
  const id = 'privacyTrackingState';

  if (
    context.site.optInPrivacyInfo &&
    (context.site.optInPrivacyInfo.canTrack || (context.site.optInPrivacyInfo.set && context.site.optInPrivacyInfo.yesSelected))
  ) {
    return { id, value: PrivacyTrackingState.CAN_TRACK };
  }

  return { id, value: PrivacyTrackingState.CAN_NOT_TRACK };
}
